import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout, BookOpen, MessageSquare, UserCircle2, X, Star, Users, Settings, LogOut, Lightbulb, Zap, Sparkles, Crown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { Logo } from '../ui/Logo';
import { trackEvent } from '@/lib/gtm';
import { NotificationPopover } from '@/components/notifications/NotificationPopover';
import { SHOW_CREATORS_FEATURE } from '@/lib/constants';
import { useFeedbackNudge } from '@/lib/feedbackNudge';
import { PricingModal } from '@/components/modals/PricingModal';

interface SidebarProps {
    isOpen?: boolean;
    onClose?: () => void;
    onOpenCreators: () => void;
    onOpenFeedback: () => void;
    onOpenCompleteProfile: () => void;
}

export const Sidebar = ({ onClose, onOpenCreators, onOpenFeedback, onOpenCompleteProfile }: SidebarProps) => {
    const location = useLocation();
    const { user, profile, signOut } = useAuth();
    const { planName, badgeStyles, tier, personasCount } = useSubscription();
    const { isNudgeVisible, dismissNudge } = useFeedbackNudge(user?.id);
    const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);

    const menuItems = [
        { id: 'dashboard', label: 'Dashboard', icon: Layout, path: '/dashboard' },
        { id: 'personas', label: 'Personas', icon: Users, path: '/personas' },
        { id: 'contexts', label: 'Contextos', icon: BookOpen, path: '/contextos' },
        { id: 'conversations', label: 'Conversas', icon: MessageSquare, path: '/conversas' },
        { id: 'insights', label: 'Insights', icon: Zap, path: '/insights', isProOnly: true },
    ];

    return (
        <>
            <div className="hidden md:flex md:relative flex-col bg-slate-900 text-slate-300 w-64 h-full shadow-xl z-50">
                <div className="p-6 flex items-center justify-between text-white border-b border-slate-800">
                    <Link to="/dashboard" className="focus:outline-none rounded-lg active:scale-[98%] transition-transform">
                        <Logo theme="dark" size="standard" />
                    </Link>
                    <button onClick={onClose} className="md:hidden text-slate-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-md p-1" aria-label="Fechar menu">
                        <X size={24} />
                    </button>
                </div>

                <nav className="flex-1 p-4 space-y-2">
                    {menuItems.map(item => {
                        const isActive = location.pathname.startsWith(item.path);
                        return (
                            <Link
                                key={item.id}
                                to={item.path}
                                onClick={onClose}
                                aria-current={isActive ? 'page' : undefined}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900 ${isActive ? 'bg-indigo-600 text-white font-medium shadow-md shadow-indigo-900/20' : 'hover:bg-slate-800 hover:text-white'}`}
                            >
                                <item.icon size={20} aria-hidden="true" />
                                <span className="flex-1">{item.label}</span>
                                {item.isProOnly && tier === 'basic' && (
                                    <span className="text-[10px] font-extrabold text-violet-300 bg-violet-500/25 border border-violet-400/30 px-1.5 py-0.5 rounded tracking-wide uppercase">
                                        PRO
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                <div className="p-4 border-t border-slate-800 space-y-3">
                    {/* Upgrade Card para usuários no Plano Basic */}
                    {tier === 'basic' && (
                        <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-950/80 to-slate-900 border border-indigo-500/30 text-white space-y-2.5 shadow-lg shadow-indigo-950/40 animate-in fade-in">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-indigo-300 flex items-center gap-1.5">
                                    <Sparkles size={14} className="text-indigo-400" /> Plano Basic
                                </span>
                                <span className="text-[11px] font-semibold text-slate-400">{personasCount}/3 Personas</span>
                            </div>
                            <button
                                onClick={() => setIsPricingModalOpen(true)}
                                className="w-full py-2 px-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all shadow-md shadow-indigo-600/30 cursor-pointer"
                            >
                                <Crown size={13} />
                                Fazer Upgrade para o Pro
                            </button>
                        </div>
                    )}

                    {/* Perfil & Ações */}
                    <div className="bg-slate-800/50 border border-slate-700/80 rounded-xl p-3 shadow-sm">
                        <Link 
                            to="/configuracoes" 
                            onClick={() => trackEvent('menu_settings_clicked')} 
                            className="flex items-center gap-3 overflow-hidden group focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg p-1 transition-all" 
                            aria-label="Acessar Configurações e Perfil"
                        >
                            <div className="w-10 h-10 shrink-0 rounded-full bg-slate-700 flex items-center justify-center border border-slate-600 group-hover:border-indigo-500 overflow-hidden transition-colors">
                                {profile?.avatar_url ? (
                                    <img src={profile.avatar_url} alt="User" className="w-full h-full object-cover" />
                                ) : (
                                    <UserCircle2 size={24} className="text-slate-300 group-hover:text-indigo-400" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0 pr-1">
                                <p className="text-sm font-bold text-white truncate group-hover:text-indigo-400 transition-colors leading-tight">
                                    {profile?.full_name || user?.email?.split('@')[0] || 'Usuário'}
                                </p>
                                <div className="mt-1">
                                    <span className={`text-[11px] font-semibold ${badgeStyles.classes} border px-2 py-0.5 rounded-md inline-flex items-center max-w-full truncate`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${badgeStyles.dotColor} shrink-0 mr-1.5`} />
                                        <span className="truncate">{planName}</span>
                                    </span>
                                </div>
                            </div>
                        </Link>

                        {/* Linha 2: Barra de Ações Rápidas com 4 Ícones (Sair, Feedback, Configurações e Notificações) */}
                        <div className="mt-3 pt-2.5 border-t border-slate-700/60 grid grid-cols-4 gap-1">
                            {/* 1. Sair */}
                            <button
                                onClick={signOut}
                                aria-label="Sair da Conta"
                                title="Sair da Conta"
                                className="p-1.5 rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors flex items-center justify-center cursor-pointer"
                            >
                                <LogOut size={16} aria-hidden="true" />
                            </button>

                            {/* 2. Dê o seu Feedback com Tooltip Nudge Inteligente */}
                            <div className="relative flex justify-center">
                                <button
                                    onClick={() => {
                                        dismissNudge();
                                        trackEvent('feedback_modal_opened');
                                        onOpenFeedback();
                                    }}
                                    aria-label="Dê o seu Feedback"
                                    title="Dê o seu Feedback"
                                    className={`p-1.5 rounded-md transition-colors flex items-center justify-center cursor-pointer ${
                                        isNudgeVisible 
                                            ? 'text-sky-400 bg-sky-500/20 ring-1 ring-sky-400/50' 
                                            : 'text-slate-400 hover:text-sky-400 hover:bg-sky-500/10 focus:outline-none focus:ring-2 focus:ring-sky-500'
                                    }`}
                                >
                                    <Lightbulb size={16} className={isNudgeVisible ? 'fill-sky-400/30' : ''} aria-hidden="true" />
                                </button>

                                {/* Tooltip Nudge Flutuante Minimalista */}
                                <AnimatePresence>
                                    {isNudgeVisible && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 8, scale: 0.92 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 8, scale: 0.92 }}
                                            transition={{ duration: 0.2 }}
                                            onClick={() => {
                                                dismissNudge();
                                                trackEvent('feedback_modal_opened');
                                                onOpenFeedback();
                                            }}
                                            className="absolute bottom-full mb-3 z-50 whitespace-nowrap bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-500 hover:to-cyan-500 border border-sky-400/50 shadow-lg shadow-sky-950/60 rounded-xl px-3 py-1.5 text-xs text-white font-bold cursor-pointer transition-all active:scale-95 pointer-events-auto"
                                        >
                                            <span>Dê o seu Feedback</span>
                                            {/* Seta para baixo */}
                                            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-cyan-600 border-r border-b border-sky-400/50 transform rotate-45" />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* 3. Configurações */}
                            <Link 
                                to="/configuracoes" 
                                onClick={() => trackEvent('menu_settings_clicked')} 
                                className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors flex items-center justify-center cursor-pointer" 
                                aria-label="Configurações" 
                                title="Configurações"
                            >
                                <Settings size={16} aria-hidden="true" />
                            </Link>

                            {/* 4. Notificações */}
                            <div className="flex justify-center">
                                <NotificationPopover onOpenCompleteProfile={onOpenCompleteProfile} placement="top" align="left" />
                            </div>
                        </div>
                    </div>

                    {SHOW_CREATORS_FEATURE && (
                        <button
                            onClick={() => {
                                trackEvent('menu_creators_clicked');
                                onOpenCreators();
                            }}
                            aria-label="Conheça os Criadores"
                            className="w-full py-2 px-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 hover:border-amber-500/40 rounded-lg text-amber-500 text-xs font-bold flex items-center justify-center gap-2 transition-all hover:bg-amber-500/20 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                            <Star size={14} className="fill-amber-500" />
                            Conheça os Criadores
                        </button>
                    )}
                </div>
            </div>

            <PricingModal
                isOpen={isPricingModalOpen}
                onClose={() => setIsPricingModalOpen(false)}
                title="Desbloqueie todo o potencial do PersonaLab no Plano Pro"
            />
        </>
    );
};
